import React, { useContext } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Slider } from '@mui/material';
import { DisableWorkflowContext } from '../../../../utils/context';
import { CalendarIcon, Plus } from '../../../../assets/images';
import SearchSelect from '../../../../components/search-select';
import { FilterDropdown, InputField } from '../../../../components';
import KeyValuePair from '../../../../components/key-value-pair';
import { configProps } from '../automator.props';

function RenderConfig({
  field,
  index,
  value,
  textareaRef,
  handleTriggerEvents,
  setWorkflowSelectModalOpen,
  handleChange,
  handleConfigurationValue,
  setOpenPayloadModal,
  dynamicVariables,
}: configProps) {
  const { disableMode } = useContext(DisableWorkflowContext);

  switch (field.element) {
    case 'input':
      return (
        <InputField
          key={`fields-${index}`}
          {...field.props}
          value={value}
          handleChange={(e) => handleConfigurationValue?.(field, e)}
          disabled={disableMode}
        />
      );

    case 'slider':
      return (
        <div className="flex flex-col text-sm w-full">
          <span className="text-[#344054] font-medium">{field.props.label}</span>
          <div className="border rounded-lg py-2 px-4">
            <Slider
              key={`fields-${index}`}
              {...field.props}
              aria-label={field.props.label}
              defaultValue={Number(value)}
              value={Number(value)}
              onChange={(e) => handleConfigurationValue?.(field, e)}
              disabled={disableMode}
              valueLabelDisplay="auto"
            />
          </div>
        </div>
      );

    case 'toggle':
      return (
        <div className="flex items-center">
          <label className="switch" htmlFor="checkbox" aria-label="checkbox">
            <input
              type="checkbox"
              checked={value === 'on'}
              onChange={(e) => handleConfigurationValue?.(field, e)}
              disabled={disableMode}
            />
            <span className="slider round" />
          </label>
          <span>{field.props.label}</span>
        </div>
      );

    case 'textarea':
      return (
        <div className="relative flex flex-col text-sm w-full">
          <span className="text-[#344054] font-medium">
            {field.props.label}
            <span className="text-red-600">*</span>
          </span>
          <textarea
            key={`fields-${index}`}
            {...field.props}
            className="mt-1 text-sm py-[5px] rounded-lg w-full border"
            onChange={(e) => {
              handleConfigurationValue?.(field, e);
              e.target.style.height = 'auto';
              e.target.style.height = `${e.target.scrollHeight}px`;
            }}
            ref={textareaRef}
            value={value}
            disabled={disableMode}
          />
          <button
            type="button"
            onClick={() => {
              setOpenPayloadModal?.(true);
            }}
            className={`absolute right-4 top-[62%] transform -translate-y-1/2 cursor-pointer w-[15px] h-[15px] ${
              disableMode && 'pointer-events-none'
            }`}
          >
            <img src={Plus} alt="add variable" />
          </button>
        </div>
      );

    case 'triggerType':
      return (
        <div
          key={`fields-${index}`}
          className={`select-trigger ${disableMode ? 'pointer-events-none' : 'cursor-pointer'}`}
        >
          <p className="text-sm font-medium text-[#6A6A6A]">
            Select {value === 'Webhook API' || value === 'Schedule' ? 'Trigger' : 'Action'}
          </p>
          <button
            type="button"
            className={`triggersName flex items-center border py-2.5 rounded-lg w-full ${
              disableMode ? 'pointer-events-none' : 'cursor-pointer'
            }`}
            onClick={() => !disableMode && setWorkflowSelectModalOpen?.(true)}
          >
            <div className="flex items-center border-2 border-[orangered] px-2 rounded-md bg-[#FDEBDA] ml-2">
              <img src={CalendarIcon} alt="trigger icon" />
              <input
                type="text"
                value={value}
                readOnly
                disabled={disableMode}
                className="cursor-pointer ml-1 font-medium text-sm bg-transparent border-none p-0 min-w-24 max-w-32 focus:shadow-none focus:outline-none focus:ring-0 focus:border-none"
              />
            </div>
            <KeyboardArrowDownIcon
              sx={{
                height: '23px',
                color: '#6B7280',
                marginLeft: 'auto',
                marginRight: '8px',
              }}
            />
          </button>
        </div>
      );

    case 'searchSelect':
      return (
        <div key={`fields-${index}`}>
          <div>
            <p className="text-sm font-medium text-[#6A6A6A]">
              {field.props.label}
              {field.props.required && <span className="text-red-600">*</span>}
            </p>
          </div>
          <div className="w-full">
            <SearchSelect
              options={field.options ?? []}
              value={value}
              onChange={(
                eValue:
                  | React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
                  | Event
                  | string,
              ) => {
                handleTriggerEvents?.((eValue as string)!);
                handleConfigurationValue?.(
                  field,
                  eValue as
                    | React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
                    | Event,
                );
              }}
              placeholder="Trigger Event"
              disabled={disableMode}
            />
          </div>
        </div>
      );

    case 'select':
      return (
        <div key={`fields-${index}`} className="flex flex-col text-sm w-full">
          <span className="text-[#344054] font-medium">
            {field.props.label}
            {field.props.required && <span className="text-red-600">*</span>}
          </span>
          <FilterDropdown
            options={
              field.options?.map((item) => ({ value: String(item.id), label: item.label })) ?? []
            }
            value={value != null ? String(value) : ''}
            onChange={(v: string) => {
              const e = {
                target: { name: field.props.name, value: v },
              } as React.ChangeEvent<HTMLSelectElement>;
              handleChange?.(e);
              handleConfigurationValue?.(field, e);
            }}
            placeholder={`Select ${field.props.label ?? 'option'}`}
            showClear={false}
            disabled={disableMode}
            className="mt-1 border border-[#E4E4E7] rounded-lg w-full px-3 py-2 text-left flex items-center justify-between"
          />
        </div>
      );

    case 'keyValuePair':
      return (
        <KeyValuePair
          {...field.props}
          key={index}
          initialKeyValue={
            field.keyValuePairs as {
              key?: string;
              value?: string;
              item?: string;
            }[]
          }
          onChange={(eValue) => {
            handleConfigurationValue?.(
              field,
              eValue as unknown as
                | Event
                | React.ChangeEvent<HTMLSelectElement | HTMLTextAreaElement | HTMLInputElement>,
            );
          }}
          field={field}
          dynamicVariables={dynamicVariables}
          disableMode={disableMode}
        />
      );
    default:
      return <div>No fields available for this type.</div>;
  }
}

export default RenderConfig;
